import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export type Role = "SUPER_ADMIN" | "ORG_ADMIN" | "MEMBER";

export interface UserSummary {
  id: string;
  email: string;
  displayName: string | null;
  role: Role;
  organizationId: string | null;
}

export interface Session {
  accessToken: string;
  accessExpiresAt: string;
  refreshToken: string;
  refreshExpiresAt: string;
  user: UserSummary;
}

interface AuthState {
  accessToken: string | null;
  accessExpiresAt: string | null;
  refreshToken: string | null;
  refreshExpiresAt: string | null;
  user: UserSummary | null;
  setSession: (s: Session) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      accessExpiresAt: null,
      refreshToken: null,
      refreshExpiresAt: null,
      user: null,
      setSession: (s) => set(s),
      clear: () =>
        set({
          accessToken: null,
          accessExpiresAt: null,
          refreshToken: null,
          refreshExpiresAt: null,
          user: null,
        }),
    }),
    {
      name: "meta-ads.auth.v1",
      storage: createJSONStorage(() => sessionStorage),
    }
  )
);

export function hasRole(min: Role): boolean {
  const u = useAuthStore.getState().user;
  if (!u) return false;
  return rolesAtLeast(u.role, min);
}

const RANK: Record<Role, number> = { SUPER_ADMIN: 100, ORG_ADMIN: 50, MEMBER: 10 };

export function rolesAtLeast(actual: Role, min: Role): boolean {
  return RANK[actual] >= RANK[min];
}
